import React from 'react';
import { ConnectDragSource } from 'react-dnd';
import { MoreVertical, Lock, MoveHorizontal, Trash2, Save } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';

/**
 * ShiftZoneControl - Unified drag/lock zone for shifts
 *
 * Reduces DOM nodes by combining the drag zone (Popover) and lock zone (Tooltip)
 * into a single component with conditional rendering.
 *
 * Before: Two separate branches with ~15 nodes each
 * After: Single component with ~8-10 nodes
 */

interface ShiftZoneControlProps {
  isProtected: boolean;
  width: number;
  dragHandleRef: ConnectDragSource;
  isDragZoneMenuOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onResize: (e: React.MouseEvent) => void;
  onDelete: (e: React.MouseEvent) => void;
  onSaveAsTemplate: (e: React.MouseEvent) => void;
  onDoubleClick: (e: React.MouseEvent) => void;
  canDelete?: boolean;
}

// Shared width-responsive classes
const getWidthClasses = (width: number): string => {
  if (width < 20) return 'w-full rounded-none';
  if (width < 40) return 'w-3 rounded-l-md';
  return 'w-4 rounded-l-md';
};

// Base zone classes shared between drag and lock modes
const baseZoneClasses = 'absolute left-0 top-0 h-full flex items-center justify-center z-20';

export const ShiftZoneControl: React.FC<ShiftZoneControlProps> = ({
  isProtected,
  width,
  dragHandleRef,
  isDragZoneMenuOpen,
  onOpenChange,
  onResize,
  onDelete,
  onSaveAsTemplate,
  onDoubleClick,
  canDelete = true,
}) => {
  const widthClasses = getWidthClasses(width);
  const showIcon = width >= 30;

  // Protected/Locked shift - just show lock icon (tooltip info is in main ShiftTooltip)
  if (isProtected) {
    return (
      <div
        className={`${baseZoneClasses} bg-black/30 dark:bg-white/10 cursor-not-allowed ${widthClasses}`}
        style={{ pointerEvents: 'auto' }}
      >
        {showIcon && <Lock className="h-3 w-3 text-white/70" />}
      </div>
    );
  }

  // Draggable shift - show popover menu with drag handle
  return (
    <Popover open={isDragZoneMenuOpen} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <div
          ref={dragHandleRef}
          className={`drag-zone ${baseZoneClasses} transition-colors bg-transparent hover:bg-black/20 dark:hover:bg-white/20 cursor-grab active:cursor-grabbing ${widthClasses}`}
          style={{ pointerEvents: 'auto' }}
          data-stop-propagation="true"
          data-drag-zone="true"
          data-tutorial="shift-drag-handle"
          onDoubleClick={onDoubleClick}
        >
          {showIcon && <MoreVertical className="h-3 w-3 text-white/90" />}
        </div>
      </PopoverTrigger>
      <PopoverContent
        side="left"
        align="start"
        className="w-auto p-1 flex flex-col gap-1"
        sideOffset={4}
      >
        <Button
          variant="ghost"
          size="sm"
          className="justify-start gap-2 h-8 px-2"
          onClick={(e) => {
            onOpenChange(false);
            onResize(e);
          }}
        >
          <MoveHorizontal className="h-4 w-4" />
          Resize
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="justify-start gap-2 h-8 px-2 text-red-600 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
          onClick={(e) => {
            onOpenChange(false);
            onDelete(e);
          }}
          disabled={!canDelete}
        >
          <Trash2 className="h-4 w-4" />
          Delete
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="justify-start gap-2 h-8 px-2"
          onClick={onSaveAsTemplate}
        >
          <Save className="h-4 w-4" />
          Save as Template
        </Button>
      </PopoverContent>
    </Popover>
  );
};

export default ShiftZoneControl;
